import React, { useCallback, useEffect, useState } from "react";
import { useStore } from "../store/useStore";
import Box from "@mui/material/Box";
import TimerIcon from "@mui/icons-material/Timer";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import Stopwatch from "./StopWatch";
import { HOVER_COLOR, TEXT_COLOR } from "../pages/root";

const FloatingWindow = () => {
  const { timerVisible, questionnaireTimerVisible, assignappId, applicationStartTime, applicacionData } = useStore();

  const questionnaires = useStore((state) => state.questionnaires);

  const [isTimeExceeded, setIsTimeExceeded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchExpanded, setIsTouchExpanded] = useState(false);

  const isTouchDevice = useMediaQuery("(hover: none) and (pointer: coarse)");
  const isCompactViewport = useMediaQuery("(max-width: 1180px)");

  const isCompactMode = isTouchDevice || isCompactViewport;

  const isTimed = applicacionData?.timed ?? true;

  const desktopWindowWidth = isTimed ? 250 : 205;

  const isShown = isTimed && (isTimeExceeded || (isCompactMode ? isTouchExpanded : isHovered));

  const showCompactTooltip = isCompactMode && !isShown;

  const progressTooltipText = isTimed
    ? `Lleva ${questionnaires.finished} de ${questionnaires.total} cuestionarios terminados. Toca para mostrar el tiempo transcurrido.`
    : `Lleva ${questionnaires.finished} de ${questionnaires.total} cuestionarios terminados.`;

  useEffect(() => {
    // Se resetea al iniciar una nueva assignapp
    setIsTimeExceeded(false);
    setIsHovered(false);
    setIsTouchExpanded(false);
  }, [assignappId, applicationStartTime]);

  const handleTimeExceeded = useCallback((exceeded) => {
    if (exceeded) {
      setIsTimeExceeded(true);
      setIsTouchExpanded(true);
    }
  }, []);

  const handleMouseEnter = () => {
    //El hover se utiliza únicamente en escritorio.
    if (!isCompactMode && !isTimeExceeded) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isCompactMode && !isTimeExceeded) {
      setIsHovered(false);
    }
  };

  const handleCompactToggle = () => {
    /*
     * En teléfonos y tablets se abre mediante toque.
     * Después de exceder el tiempo permanece abierto.
     */
    if (!isCompactMode || !isTimed || isTimeExceeded) {
      return;
    }

    setIsTouchExpanded((currentValue) => !currentValue);
  };

  const handleKeyboardToggle = (event) => {
    if (!isTimed || isTimeExceeded) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();

      if (isCompactMode) {
        setIsTouchExpanded((currentValue) => !currentValue);
      }
    }
  };

  if (!timerVisible) return null;

  return (
    <Tooltip
      title={progressTooltipText}
      arrow
      placement="bottom"
      enterDelay={100}
      enterTouchDelay={200}
      leaveDelay={0}
      leaveTouchDelay={2000}
      disableInteractive
      describeChild
      disableHoverListener={!showCompactTooltip}
      disableFocusListener={!showCompactTooltip}
      disableTouchListener={!showCompactTooltip}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: {
              xs: 240,
              sm: 300,
            },
            px: {
              xs: 1.25,
              sm: 1.5,
            },
            py: {
              xs: 0.75,
              sm: 1,
            },
            fontSize: {
              xs: "11px",
              sm: "12px",
            },
            lineHeight: 1.35,
            textAlign: "center",
          },
        },
      }}
    >
      <Box
        role={isTimed ? "button" : undefined}
        tabIndex={isTimed ? 0 : undefined}
        aria-expanded={isTimed ? isShown : undefined}
        aria-label={isTimed ? `${progressTooltipText} Mostrar u ocultar el tiempo transcurrido.` : progressTooltipText}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleCompactToggle}
        onKeyDown={handleKeyboardToggle}
        sx={{
          position: "fixed",
          top: {
            xs: process.env.REACT_APP_NODE_ENV === "staging" ? 110 : 78,
            sm: process.env.REACT_APP_NODE_ENV === "staging" ? 116 : 82,
          },

          right: {
            xs: 10,
            sm: 16,
            lg: 30,
          },

          width: isCompactMode
            ? isShown
              ? {
                  xs: "calc(100vw - 20px)",
                  sm: 280,
                }
              : "fit-content"
            : desktopWindowWidth,

          minWidth: isCompactMode
            ? isShown
              ? 0
              : {
                  xs: 130,
                  sm: 145,
                }
            : desktopWindowWidth,

          maxWidth: isCompactMode
            ? {
                xs: "calc(100vw - 20px)",
                sm: 280,
              }
            : desktopWindowWidth,

          minHeight: !isTimed ? (isCompactMode ? 40 : 52) : isCompactMode ? (isShown ? 105 : 40) : isShown ? 118 : 76,

          boxSizing: "border-box",

          backgroundColor: isTimeExceeded ? "#ef233c" : HOVER_COLOR,

          borderRadius:
            isCompactMode && !isShown
              ? "999px"
              : {
                  xs: "16px",
                  sm: "18px",
                  lg: "20px",
                },

          fontSize: {
            xs: "11px",
            sm: "12px",
            lg: "14px",
          },

          lineHeight: 1.35,

          padding:
            isCompactMode && !isShown
              ? {
                  xs: "6px 10px",
                  sm: "7px 12px",
                }
              : {
                  xs: "11px 13px",
                  sm: "12px 15px",
                  lg: "14px 18px",
                },

          color: TEXT_COLOR,
          zIndex: 40,

          cursor: isTimed ? "pointer" : "default",

          textAlign: "center",

          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",

          overflow: "hidden",
          overflowWrap: "anywhere",

          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",

          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.16)",

          transition: [
            "width 0.25s ease-in-out",
            "min-width 0.25s ease-in-out",
            "min-height 0.25s ease-in-out",
            "padding 0.25s ease-in-out",
            "border-radius 0.25s ease-in-out",
            "background-color 0.3s ease-in-out",
            "box-shadow 0.25s ease-in-out",
          ].join(", "),

          "&:hover": {
            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.2)",
          },

          "&:focus-visible": {
            outline: "3px solid rgba(255, 255, 255, 0.8)",
            outlineOffset: "2px",
          },
        }}
      >
        {isCompactMode && !isShown && (
          <Box
            sx={{
              width: "100%",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              gap: 0.5,

              whiteSpace: "nowrap",
              textAlign: "center",
            }}
          >
            {isTimed && (
              <TimerIcon
                sx={{
                  flexShrink: 0,

                  fontSize: {
                    xs: 16,
                    sm: 17,
                  },
                }}
              />
            )}

            <Box
              component="span"
              sx={{
                fontWeight: 600,
                lineHeight: 1.15,
              }}
            >
              {questionnaires.finished} / {questionnaires.total} cuestionarios
            </Box>
          </Box>
        )}

        {/*
         * En escritorio siempre se muestra el texto completo.
         * En móvil y tablet se muestra solamente al abrir la tarjeta.
         */}
        {(!isCompactMode || isShown) && (
          <Box
            component="span"
            sx={{
              width: "100%",

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              px: 0.5,

              fontWeight: 400,
              lineHeight: 1.35,
              textAlign: "center",
            }}
          >
            Lleva {questionnaires.finished} / {questionnaires.total} cuestionarios terminados
          </Box>
        )}

        {/* Información del cronómetro */}
        {isTimed && (
          <Box
            sx={{
              width: "100%",

              display: "grid",
              gridTemplateRows: isShown ? "1fr" : "0fr",

              opacity: isShown ? 1 : 0,
              visibility: isShown ? "visible" : "hidden",

              transition: ["grid-template-rows 0.25s ease-in-out", "opacity 0.2s ease-in-out", "visibility 0.2s ease-in-out"].join(", "),
            }}
          >
            <Box
              sx={{
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "100%",

                  mt: 1,
                  pt: 1,

                  borderTop: "1px solid rgba(0, 0, 0, 0.14)",

                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",

                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: "100%",

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    flexWrap: "wrap",

                    columnGap: 0.5,
                    rowGap: 0.25,

                    textAlign: "center",
                  }}
                >
                  <TimerIcon
                    sx={{
                      flexShrink: 0,

                      fontSize: {
                        xs: 18,
                        sm: 20,
                        lg: 22,
                      },
                    }}
                  />

                  <Box
                    component="span"
                    sx={{
                      fontWeight: 500,
                      lineHeight: 1.25,
                    }}
                  >
                    {questionnaireTimerVisible ? "Tiempo del cuestionario:" : "Tiempo de la aplicación:"}
                  </Box>
                </Box>

                <Box
                  sx={{
                    width: "100%",

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    textAlign: "center",
                  }}
                >
                  <Stopwatch timerVisible={timerVisible && isTimed} onTimeExceeded={handleTimeExceeded} />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {isTimed && !isCompactMode && (
          <Box
            component="span"
            sx={{
              width: "100%",

              height: isShown ? 0 : 20,
              mt: isShown ? 0 : 0.5,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              fontSize: "14px",
              lineHeight: 1.2,
              fontStyle: "italic",
              textAlign: "center",

              color: "rgba(55, 65, 81, 0.9)",

              opacity: isShown ? 0 : 1,
              visibility: isShown ? "hidden" : "visible",

              overflow: "hidden",
              pointerEvents: "none",

              transition: ["height 0.2s ease-in-out", "margin-top 0.2s ease-in-out", "opacity 0.2s ease-in-out", "visibility 0.2s ease-in-out"].join(
                ", ",
              ),
            }}
          >
            Ver el tiempo transcurrido
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default FloatingWindow;
